import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useState,
	type ReactNode,
} from 'react';
import { useConvex } from 'convex/react';

import { api } from '@/convex/_generated/api';
import {
	clearMemberBootstrapStorage,
	completeMemberBootstrap,
	createMemberBootstrapAuthorizeUrl,
	memberBootstrapEnabled,
	memberMigrationAuthenticationAllowed,
	restoreMemberBootstrapSession,
	type MemberBootstrapSession,
	type MemberMigrationState,
} from '@/lib/member-bootstrap';
import { clearMemberWalletSessions } from '@/lib/member-wallet-rpc';
import { readMemberMigrationState } from '@/lib/member-wallet-transport';
import { diagnosticFailureClass } from '@/lib/value-free-diagnostics';
import {
	createMemberSigner,
	type MemberSigner,
	usePrivyMemberSignerRuntime,
} from './MemberSignerProvider';

type User = {
	_id: string;
	email: string;
	name?: string;
	walletAddress?: string;
	role?: string;
	needsWalletSetup?: boolean;
	founderPassProcessed?: boolean;
};

type Web3AuthContextType = {
	user: User | null;
	walletAddress: string | null;
	isWeb3AuthLoading: boolean;
	isLoggedIn: boolean;
	authAttemptCompleted: boolean;
	logout: () => Promise<void>;
	memberSigner: MemberSigner | null;
	getMemberAuthToken: (() => Promise<string>) | null;
	memberBootstrapEnabled: boolean;
	memberMigrationState: MemberMigrationState | null;
	memberBootstrapError: string | null;
	startMemberLogin: () => Promise<void>;
};

const Web3AuthContext = createContext<Web3AuthContextType | undefined>(
	undefined,
);

export const Web3AuthProvider = ({ children }: { children: ReactNode }) => {
	const bootstrapEnabled = memberBootstrapEnabled(
		import.meta.env.VITE_WATCHER_MEMBER_BOOTSTRAP_ENABLED,
	);
	const [user, setUser] = useState<User | null>(null);
	const [walletAddress, setWalletAddress] = useState<string | null>(null);
	const [isWeb3AuthLoading, setIsWeb3AuthLoading] = useState(true);
	const [authAttemptCompleted, setAuthAttemptCompleted] = useState(false);
	const [bootstrapSession, setBootstrapSession] =
		useState<MemberBootstrapSession | null>(null);
	const [memberMigrationState, setMemberMigrationState] =
		useState<MemberMigrationState | null>(null);
	const [memberBootstrapError, setMemberBootstrapError] = useState<
		string | null
	>(null);
	const bootstrapEpoch = React.useRef(0);
	const bootstrapAbort = React.useRef<AbortController | null>(null);
	const convex = useConvex();
	const privyMemberSigner = usePrivyMemberSignerRuntime();

	const resolveBootstrapUser = useCallback(
		async (
			session: Omit<MemberBootstrapSession, 'migrationState'>,
		): Promise<User> => {
			const dbUser = await convex.query(api.users.getByEmail, {
				email: session.canonicalEmail,
			});
			if (
				!dbUser ||
				String(dbUser._id) !== session.canonicalUserId ||
				String(dbUser.email).toLowerCase() !==
					session.canonicalEmail.toLowerCase() ||
				typeof dbUser.walletAddress !== 'string' ||
				dbUser.walletAddress.toLowerCase() !==
					session.canonicalWalletAddress.toLowerCase()
			) {
				throw new Error('MEMBER_BOOTSTRAP_IDENTITY_MISMATCH');
			}
			return {
				_id: dbUser._id,
				email: dbUser.email,
				name: dbUser.name,
				walletAddress: dbUser.walletAddress,
				role: dbUser.role,
				needsWalletSetup: dbUser.needsWalletSetup,
				founderPassProcessed: dbUser.founderPassProcessed,
			};
		},
		[convex],
	);

	useEffect(() => {
		if (!bootstrapEnabled) {
			setUser(null);
			setWalletAddress(null);
			setBootstrapSession(null);
			setMemberMigrationState(null);
			setAuthAttemptCompleted(true);
			setIsWeb3AuthLoading(false);
			return;
		}

		const epoch = ++bootstrapEpoch.current;
		const controller = new AbortController();
		bootstrapAbort.current?.abort();
		bootstrapAbort.current = controller;
		const current = () =>
			bootstrapEpoch.current === epoch && !controller.signal.aborted;

		const restore = async () => {
			setIsWeb3AuthLoading(true);
			setAuthAttemptCompleted(false);
			setMemberBootstrapError(null);
			try {
				if (window.location.pathname === '/auth/callback') {
					await completeMemberBootstrap({
						callbackUrl: window.location.href,
						storage: sessionStorage,
						readMigrationState: readMemberMigrationState,
						signal: controller.signal,
					});
					if (current()) window.location.replace('/admin');
					return;
				}

				const stored = restoreMemberBootstrapSession({ storage: sessionStorage });
				if (!stored) {
					if (!current()) return;
					setBootstrapSession(null);
					setMemberMigrationState(null);
					setUser(null);
					setWalletAddress(null);
					setAuthAttemptCompleted(true);
					setIsWeb3AuthLoading(false);
					return;
				}

				const migrationState = await readMemberMigrationState(
					stored.token,
					controller.signal,
				);
				if (!memberMigrationAuthenticationAllowed(migrationState)) {
					throw new Error('MEMBER_MIGRATION_PROVIDER_UNAVAILABLE');
				}
				const dbUser = await resolveBootstrapUser(stored);
				if (!current()) return;
				setBootstrapSession({ ...stored, migrationState });
				setMemberMigrationState(migrationState);
				setUser(dbUser);
				setWalletAddress(dbUser.walletAddress ?? null);
				setAuthAttemptCompleted(true);
				setIsWeb3AuthLoading(false);
			} catch (error) {
				if (!current()) return;
				clearMemberBootstrapStorage(sessionStorage);
				setBootstrapSession(null);
				setMemberMigrationState(null);
				setUser(null);
				setWalletAddress(null);
				setMemberBootstrapError(
					error instanceof Error ? error.message : 'MEMBER_BOOTSTRAP_FAILED',
				);
				setAuthAttemptCompleted(true);
				setIsWeb3AuthLoading(false);
				if (window.location.pathname === '/auth/callback') {
					window.location.replace('/');
				}
			}
		};

		void restore();
		return () => controller.abort();
	}, [bootstrapEnabled, resolveBootstrapUser]);

	const startMemberLogin = useCallback(async () => {
		if (!bootstrapEnabled) throw new Error('MEMBER_BOOTSTRAP_DISABLED');
		const epoch = ++bootstrapEpoch.current;
		bootstrapAbort.current?.abort();
		clearMemberBootstrapStorage(sessionStorage);
		setMemberBootstrapError(null);
		const authorizeUrl = await createMemberBootstrapAuthorizeUrl({
			storage: sessionStorage,
			origin: window.location.origin,
		});
		if (bootstrapEpoch.current !== epoch) {
			clearMemberBootstrapStorage(sessionStorage);
			throw new Error('MEMBER_BOOTSTRAP_CANCELLED');
		}
		window.location.assign(authorizeUrl);
	}, [bootstrapEnabled]);

	const canonicalAddress = user?.walletAddress ?? null;
	const getCanonicalMemberAuthToken = useCallback(() => {
		if (
			!bootstrapEnabled ||
			!bootstrapSession ||
			bootstrapSession.exp <= Date.now() + 30_000 ||
			bootstrapSession.canonicalWalletAddress.toLowerCase() !==
				canonicalAddress?.toLowerCase()
		) {
			return Promise.reject(new Error('MEMBER_SESSION_UNAVAILABLE'));
		}
		return Promise.resolve(bootstrapSession.token);
	}, [bootstrapEnabled, bootstrapSession, canonicalAddress]);

	const refreshMemberMigrationState = useCallback(async () => {
		const epoch = bootstrapEpoch.current;
		const token = await getCanonicalMemberAuthToken();
		const state = await readMemberMigrationState(token);
		if (bootstrapEpoch.current !== epoch) {
			throw new Error('MEMBER_BOOTSTRAP_CANCELLED');
		}
		setMemberMigrationState(state);
		return state;
	}, [getCanonicalMemberAuthToken]);

	useEffect(() => {
		privyMemberSigner.setCanonicalMemberSession({
			canonicalAccountAddress: canonicalAddress,
			authenticated: Boolean(user),
			isLoading: isWeb3AuthLoading,
			getAuthToken:
				bootstrapEnabled && bootstrapSession
					? getCanonicalMemberAuthToken
					: null,
		});
	}, [
		bootstrapEnabled,
		bootstrapSession,
		canonicalAddress,
		getCanonicalMemberAuthToken,
		isWeb3AuthLoading,
		privyMemberSigner.setCanonicalMemberSession,
		user,
	]);

	const memberSigner = React.useMemo(
		() =>
			createMemberSigner({
				canonicalAccountAddress: canonicalAddress,
				privy: privyMemberSigner,
				getMemberAuthToken:
					bootstrapEnabled && bootstrapSession
						? getCanonicalMemberAuthToken
						: null,
				enforceMigrationState: bootstrapEnabled,
				migrationState: memberMigrationState,
				getCurrentMigrationState:
					bootstrapEnabled && bootstrapSession
						? refreshMemberMigrationState
						: null,
			}),
		[
			bootstrapEnabled,
			bootstrapSession,
			canonicalAddress,
			getCanonicalMemberAuthToken,
			memberMigrationState,
			privyMemberSigner,
			refreshMemberMigrationState,
		],
	);

	const logout = useCallback(async () => {
		++bootstrapEpoch.current;
		bootstrapAbort.current?.abort();
		clearMemberBootstrapStorage(sessionStorage);
		clearMemberWalletSessions();
		setBootstrapSession(null);
		setMemberMigrationState(null);
		setUser(null);
		setWalletAddress(null);
		setAuthAttemptCompleted(true);
		setIsWeb3AuthLoading(false);
		try {
			await privyMemberSigner.logout();
		} catch (error) {
			console.error('[Web3AuthProvider] Error clearing Privy signer state:', {
				failureClass: diagnosticFailureClass(error),
			});
		}
		sessionStorage.removeItem('loginProcessedForThisSession');
		sessionStorage.removeItem('hasNavigatedToAdmin');
		window.location.href = '/';
	}, [privyMemberSigner]);

	return (
		<Web3AuthContext.Provider
			value={{
				user,
				walletAddress,
				isWeb3AuthLoading,
				isLoggedIn: Boolean(user),
				authAttemptCompleted,
				logout,
				memberSigner,
				getMemberAuthToken:
					bootstrapEnabled && bootstrapSession
						? getCanonicalMemberAuthToken
						: null,
				memberBootstrapEnabled: bootstrapEnabled,
				memberMigrationState,
				memberBootstrapError,
				startMemberLogin,
			}}
		>
			{children}
		</Web3AuthContext.Provider>
	);
};

export const useWeb3Auth = (): Web3AuthContextType => {
	const context = useContext(Web3AuthContext);
	if (context === undefined) {
		throw new Error('useWeb3Auth must be used within a Web3AuthProvider');
	}
	return context;
};
